import React from "react";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import Manifesto from "../components/Manifesto";
import SectionTitle from "../components/SectionTitle";
import ContactForm from "../components/ContactForm";
import PricingCard from "../components/PricingCard";
import Reveal from "../components/Reveal";
import FeaturedTestimonial from "../components/FeaturedTestimonial";
import UniversCarousel from "../components/UniversCarousel";
import { useLang } from "../context/LangContext";
import { ASSETS, TREATMENTS } from "../lib/content";
import { Link } from "react-router-dom";
import { pathFor } from "../lib/routes";
import { ArrowUpRight, Anchor, Trophy, Cpu, Activity } from "lucide-react";

export default function Home() {
  const { lang, t } = useLang();
  const p = (k) => pathFor(k, lang);

  return (
    <main>
      <Hero />
      <Marquee />

      {/* Partnership banner */}
      <section className="bg-[var(--surface-2)] hairline-b" data-testid="partnership-banner">
        <div className="container-luxe py-5 flex items-center justify-center gap-4 text-center">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)] shrink-0" />
          <span className="font-display text-[11px] md:text-[12px] uppercase tracking-[0.22em] text-[var(--ink)]/85">
            {t("stats.partnership_banner")}
          </span>
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)] shrink-0" />
        </div>
      </section>

      {/* Univers carousel — tout en haut */}
      <UniversCarousel />

      <Manifesto />

      {/* Treatments */}
      <section className="container-luxe py-24 md:py-32" data-testid="section-treatments">
        <SectionTitle eyebrow="02 — PROTOCOLES" title={t("home.treatments_title")} subtitle={t("home.treatments_sub")} />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {TREATMENTS.slice(0, 6).map((tr, i) => (
            <Reveal key={tr.key} delay={i * 0.08} className="h-full">
              <div className="beam-card p-6 md:p-7 h-full" data-testid={`treatment-card-${tr.key}`}>
                <div className="flex items-center justify-between">
                  <span className="overline">0{i + 1}</span>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[var(--ink)]/40 font-display">{tr[`price_${lang}`] || tr.price}</span>
                </div>
                <h3 className="font-display text-xl md:text-2xl text-[var(--ink)] mt-6 font-light">{tr[lang] || tr.fr}</h3>
                <p className="mt-3 text-sm text-[var(--text-secondary)] leading-relaxed">{tr[`desc_${lang}`] || tr.desc_fr}</p>
                <Link to={p("treatments")} className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-display text-[var(--gold)]">
                  {t("cta.learn_more")} <ArrowUpRight size={14} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Yacht */}
      <section className="bg-surface-1 hairline-t hairline-b py-24 md:py-32" data-testid="section-yacht">
        <div className="container-luxe grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <SectionTitle eyebrow="03 — À BORD" title={t("home.yacht_title")} subtitle={t("home.yacht_sub")} />
            <Link to={p("yacht")} className="btn-ghost mt-8" data-testid="link-yacht">
              <Anchor size={14} /> {t("cta.learn_more")}
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-7 relative" delay={0.12}>
            <img src={ASSETS.yacht} alt="Monaco yacht" className="w-full h-[420px] object-cover rounded-md hairline" />
            <div className="absolute -bottom-4 -left-4 hidden md:block bg-base hairline p-5">
              <div className="overline">SLA</div>
              <div className="font-display text-2xl text-[var(--ink)]">90&nbsp;min</div>
              <div className="text-xs text-[var(--ink)]/50">marina delivery</div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Villa & Conciergerie */}
      <section className="container-luxe py-24 md:py-32" data-testid="section-villa">
        <SectionTitle
          eyebrow="04 — VILLA & CONCIERGERIE"
          title={lang === "fr" ? "Un protocole à domicile, orchestré par votre conciergerie." : lang === "it" ? "Un protocollo a domicilio, orchestrato dalla vostra conciergerie." : lang === "ru" ? "Протокол на дому под управлением вашего консьержа." : "A home-delivered protocol, orchestrated by your concierge."}
          subtitle={lang === "fr" ? "Intervention physique sur Monaco & Côte d'Azur — et par hélicoptère jusqu'à 400 km à vol d'oiseau, sur demande." : lang === "it" ? "Intervento fisico su Monaco e Costa Azzurra — ed in elicottero fino a 400 km in linea d'aria, su richiesta." : lang === "ru" ? "Наземный выезд по Монако и Лазурному берегу — и на вертолёте до 400 км по прямой, по запросу." : "Ground delivery across Monaco & French Riviera — and by helicopter up to 400 km as the crow flies, on request."}
        />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px hairline">
          {[
            { k: lang === "fr" ? "Villa privée" : lang === "it" ? "Villa privata" : lang === "ru" ? "Частная вилла" : "Private villa", v: lang === "fr" ? "Installation complète, discrétion totale." : lang === "it" ? "Installazione completa, discrezione totale." : lang === "ru" ? "Полная установка, абсолютная конфиденциальность." : "Full setup, total discretion." },
            { k: lang === "fr" ? "Conciergerie" : lang === "it" ? "Conciergerie" : lang === "ru" ? "Консьерж" : "Concierge", v: lang === "fr" ? "Un interlocuteur unique, planning confidentiel." : lang === "it" ? "Un interlocutore unico, pianificazione riservata." : lang === "ru" ? "Единая точка контакта, конфиденциальный график." : "Single point of contact, confidential planning." },
            { k: lang === "fr" ? "Hélicoptère" : lang === "it" ? "Elicottero" : lang === "ru" ? "Вертолёт" : "Helicopter", v: lang === "fr" ? "Jusqu'à 400 km via nos partenaires aériens." : lang === "it" ? "Fino a 400 km con i nostri partner aerei." : lang === "ru" ? "До 400 км с нашими авиапартнёрами." : "Up to 400 km via our air partners." },
          ].map((s, i) => (
            <Reveal key={s.k} delay={i * 0.1} y={20} className="bg-surface-1">
              <div className="p-8 flex flex-col min-h-[200px]">
                <Trophy size={18} className="text-[var(--gold)]" />
                <div className="overline mt-auto pt-8">{s.k}</div>
                <div className="font-display text-lg text-[var(--ink)] mt-2 font-light leading-snug">{s.v}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Tech XP + Exomobil */}
      <section className="bg-surface-1 hairline-t hairline-b py-24 md:py-32" data-testid="section-tech">
        <div className="container-luxe">
          <SectionTitle eyebrow="05 — TECHNOLOGIE" title={t("home.tech_title")} subtitle={t("home.tech_sub")} />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Reveal delay={0}>
              <Link to={p("xpTherapy")} className="beam-card overflow-hidden group block" data-testid="card-xp">
                <img src={ASSETS.shockwave} alt="Notre Thérapie" className="w-full h-72 object-cover opacity-90 group-hover:opacity-100 transition" />
                <div className="p-7">
                  <Cpu size={18} className="text-[var(--gold)]" />
                  <h3 className="font-display text-2xl text-[var(--ink)] mt-3 font-light">{lang === "fr" ? "Notre Thérapie" : lang === "it" ? "La nostra terapia" : lang === "ru" ? "Наша терапия" : "Our Therapy"}</h3>
                  <p className="text-sm text-[var(--text-secondary)] mt-3">{lang === "fr" ? "Plateforme instrumentée, monitoring temps réel, IA assistée." : "Instrumented platform, real-time monitoring, AI-assisted."}</p>
                </div>
              </Link>
            </Reveal>
            <Reveal delay={0.12}>
              <Link to={p("exomobil")} className="beam-card overflow-hidden group block" data-testid="card-exo">
                <img src={ASSETS.exomobil} alt="Exosquelette" className="w-full h-72 object-cover opacity-90 group-hover:opacity-100 transition" />
                <div className="p-7">
                  <Activity size={18} className="text-[var(--gold)]" />
                  <h3 className="font-display text-2xl text-[var(--ink)] mt-3 font-light">{lang === "fr" ? "Exosquelette" : lang === "it" ? "Esoscheletro" : lang === "ru" ? "Экзоскелет" : "Exoskeleton"}</h3>
                  <p className="text-sm text-[var(--text-secondary)] mt-3">{lang === "fr" ? "Unité de soin mobile premium. Confidentialité absolue, performances cliniques." : "Premium mobile care unit. Absolute privacy, clinical performance."}</p>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="bg-surface-1 hairline-t hairline-b py-16" data-testid="section-partners">
        <div className="container-luxe">
          <div className="overline mb-8">{t("stats.trusted_by")}</div>
          <div className="flex flex-wrap items-center gap-x-12 gap-y-6">
            <div className="font-serif-luxe italic text-base md:text-lg text-[var(--ink)]/80">MedMaiora Medical Center</div>
            <div className="font-serif-luxe italic text-base md:text-lg text-[var(--ink)]/80">BeLonG Group</div>
            <div className="font-serif-luxe italic text-base md:text-lg text-[var(--ink)]/80">Riviera Wellness Center</div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="container-luxe py-24 md:py-32" data-testid="section-testimonials">
        <SectionTitle eyebrow="06 — VOIX" title={t("home.testimonials_title")} />
        <FeaturedTestimonial />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            {
              img: null,
              name: "Philippe Corti",
              role_fr: "Comédien & DJ français",
              role_en: "French actor & DJ",
              role_it: "Attore & DJ francese",
              role_ru: "Французский актёр и DJ",
              quote_fr: "C'est incroyable. Je ne pouvais pas me baisser, ni même faire mes lacets, ni marcher. Et là, je revis.",
              quote_en: "It's incredible. I couldn't bend down, couldn't even tie my shoelaces, couldn't walk. And now, I'm alive again.",
              quote_it: "È incredibile. Non riuscivo a chinarmi, nemmeno ad allacciarmi le scarpe, né a camminare. E ora rivivo.",
              quote_ru: "Это невероятно. Я не мог наклониться, даже завязать шнурки, не мог ходить. А теперь я снова живу.",
              public: true,
            },
            {
              img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop&q=80",
              name: "A. R.",
              role_fr: "Propriétaire yacht 42m",
              role_en: "42m yacht owner",
              quote_fr: "Notre thérapie combinée à l'équipe Riviera, c'est ce que j'ai vu de plus avancé en récupération onboard.",
              quote_en: "Our therapy combined with the Riviera team is the most advanced onboard recovery I have seen.",
            },
            {
              img: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&q=80",
              name: "S. K.",
              role_fr: "Hôte privé · Cap-Ferrat",
              role_en: "Private host · Cap-Ferrat",
              quote_fr: "Notre thérapie apporte une dimension scientifique au protocole. Mes invités le ressentent dès la première session.",
              quote_en: "Our therapy adds a scientific layer to the protocol. My guests feel it from the first session.",
            },
            {
              img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&q=80",
              name: "C. D.",
              role_fr: "Capitaine yacht",
              role_en: "Yacht captain",
              quote_fr: "Le monitoring temps réel de notre thérapie permet d'objectiver la récupération de l'équipage. C'est précieux.",
              quote_en: "Our therapy's real-time monitoring makes crew recovery measurable. Invaluable.",
            },
            {
              img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop&q=80",
              name: "I. V.",
              role_fr: "Performance coach",
              role_en: "Performance coach",
              quote_fr: "Aucun autre protocole mobile n'intègre une plateforme aussi instrumentée. Les données parlent d'elles-mêmes.",
              quote_en: "No other mobile protocol integrates such an instrumented platform. The data speaks for itself.",
            },
            {
              img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&q=80",
              name: "P. H.",
              role_fr: "Family Office · Monaco",
              role_en: "Family Office · Monaco",
              quote_fr: "Notre thérapie + l'équipe médicale Riviera : un standard de récupération que nous avons adopté à la maison.",
              quote_en: "Our therapy + the Riviera medical team: a recovery standard we have adopted at our estate.",
            },
            {
              img: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&h=400&fit=crop&q=80",
              name: "L. M.",
              role_fr: "Athlète professionnel",
              role_en: "Professional athlete",
              quote_fr: "Notre thérapie m'a permis de comprendre ma récupération, pas seulement la subir.",
              quote_en: "Our therapy let me understand my recovery, not just go through it.",
            },
          ].map((tt, i) => (
            <Reveal key={i} delay={(i % 3) * 0.09} className="h-full">
              <div className="beam-card p-7 flex flex-col h-full" data-testid={`testimonial-${i}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-display text-base text-[var(--ink)]">{tt.name}</div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-[var(--text-secondary)] font-display mt-0.5">{tt[`role_${lang}`] || tt.role_en}</div>
                  </div>
                  {tt.public ? (
                    <span className="text-[9px] uppercase tracking-[0.22em] text-white font-display bg-[var(--gold)] rounded-full px-2.5 py-1" data-testid={`testimonial-verified-${i}`}>
                      {lang === "fr" ? "Vérifié" : lang === "it" ? "Verificato" : lang === "ru" ? "Проверено" : "Verified"}
                    </span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-[0.22em] text-[var(--gold)]/80 font-display border border-[var(--gold)]/30 rounded-full px-2.5 py-1" data-testid={`testimonial-confidential-${i}`}>
                      {lang === "fr" ? "Confidentiel" : lang === "it" ? "Riservato" : lang === "ru" ? "Конфиденциально" : "Confidential"}
                    </span>
                  )}
                </div>
                <p className="font-serif-luxe text-lg md:text-xl text-[var(--ink)]/90 italic leading-snug mt-6">"{tt[`quote_${lang}`] || tt.quote_en}"</p>
                <div className="mt-auto pt-5 flex gap-1 text-[var(--gold)]" aria-label="5 étoiles">
                  {[1,2,3,4,5].map((n) => <span key={n}>★</span>)}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Pricing — Plaquette officielle */}
      <section className="bg-surface-1 hairline-t hairline-b py-24 md:py-32" data-testid="section-pricing">
        <div className="container-luxe">
          <SectionTitle eyebrow="07 — TARIFS & CONDITIONS" title={t("home.pricing_title")} subtitle={lang === "fr" ? "Yacht · Villa · Hôtel · Club · Événement privé. Équipe et équipements se déplacent sur votre lieu de vie ou de séjour." : "Yacht · Villa · Hotel · Club · Private event. Team and equipment travel to your location."} />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px hairline">
            {TREATMENTS.map((item, i) => (
              <PricingCard key={item.key} item={item} index={i} />
            ))}
          </div>
          <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-[var(--ink)]/40 font-display">
            {lang === "fr" ? "TVA et éventuels frais précisés sur devis · Prestation ne remplaçant pas un suivi médical" : "VAT and additional fees detailed on quote · Service does not replace required medical follow-up"}
          </p>
        </div>
      </section>

      {/* Contact */}
      <section className="container-luxe py-24 md:py-32" data-testid="section-contact">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <SectionTitle eyebrow="08 — CONTACT" title={t("home.contact_title")} subtitle={t("home.contact_sub")} />
            <div className="mt-8 space-y-2 text-sm text-[var(--ink)]/70">
              <div>contact@riviera-shockwaves.com</div>
              <div>+33 7 86 44 82 47 · WhatsApp</div>
              <div>Cala del Forte · Monaco · Cannes · Saint-Tropez</div>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={0.12}>
            <ContactForm source="home" />
          </Reveal>
        </div>
      </section>

      {/* Investor CTA */}
      <section className="container-luxe pb-24" data-testid="section-investor-cta">
        <Link to={p("investors")} className="hairline-t pt-10 flex items-center justify-between group">
          <span className="font-serif-luxe italic text-xl md:text-2xl text-[var(--ink)]/80">{t("home.investor_cta")} →</span>
          <span className="overline">PRIVATE</span>
        </Link>
      </section>
    </main>
  );
}
